<script>
  import { goto } from '$app/navigation';
  import Badge from '$lib/components/Badge.svelte';
  import Button from '$lib/components/Button.svelte';
  import Card from '$lib/components/Card.svelte';
  import Icon from '$lib/components/Icon.svelte';
  import { showModal, showToast } from '$lib/features/app/modals';
  import { user } from '$lib/features/auth/authStore';
  import { useRolesQuery } from '$lib/features/clients/service';
  import { generateFollowUpMessage } from '$lib/features/messages/services';
  import { userQueryOptions } from '$lib/features/user/services';
  import { logger } from '$lib/logger';
  import { createMutation, createQuery } from '@tanstack/svelte-query';
  import { format, parseISO } from 'date-fns';
  import { toStore } from 'svelte/store';
  import { twMerge } from 'tailwind-merge';
  import { NOTE_TYPES } from '../../constants';
  import DeleteNoteModal from '../modals/DeleteNoteModal.svelte';
  import DeleteSavedNoteModal from '../modals/DeleteSavedNoteModal.svelte';
  import EditConsultationModal from '../modals/EditConsultationModal.svelte';
  import SignSavedNoteModal from '../modals/SignSavedNoteModal.svelte';

  let { note, areaOptions, onTranscriptionClick, transcriptionId } = $props();

  const generateFollowUpMutation = createMutation({
    mutationKey: ['generateFollowUpMessage'],
    mutationFn: generateFollowUpMessage,
  });

  let consultationAreas = $derived(
    note.consultationAreas.map(
      ({ id }) => areaOptions.find((area) => area.value === id)?.label,
    ),
  );

  let deletedByFullName = $state('');
  let userRole = $state('');

  const deletedByUserQuery = createQuery(
    toStore(() => ({
      ...userQueryOptions(note.deletedByUserId),
      enabled: !!note.deletedByUserId,
    })),
  );

  // get all user roles
  const rolesQuery = useRolesQuery(
    {},
    {
      gcTime: 1000 * 60 * 60 * 24,
      staleTime: 1000 * 60 * 60 * 24,
    },
  );

  function handleDeleteNoteClick() {
    showModal(DeleteNoteModal, {
      note,
    });
  }

  let noteClasses = $derived(
    twMerge(
      'whitespace-pre-line',
      note.deleted && 'line-through hover:no-underline',
    ),
  );

  // $effect.pre: seeds state the template renders, so it must land before
  // the DOM update or the field paints empty for a frame.
  $effect.pre(() => {
    // create a full name of the user who deleted the note
    if ($deletedByUserQuery?.data) {
      deletedByFullName =
        $deletedByUserQuery.data.firstname +
        ' ' +
        $deletedByUserQuery.data.lastname;
    }

    // get the role of the user who created the note
    if ($rolesQuery?.data) {
      userRole = $rolesQuery?.data?.find((role) => role.id === note.role);
    }
  });

  function getNoteTypeText(noteType) {
    return NOTE_TYPES[noteType] || '';
  }

  function editNote() {
    showModal(EditConsultationModal, {
      note,
      areaOptions,
    });
  }
  function deleteNote() {
    showModal(DeleteSavedNoteModal, {
      note,
    });
  }

  function signNote() {
    showModal(SignSavedNoteModal, {
      note,
    });
  }

  function handleGenerateFollowUpMessage() {
    $generateFollowUpMutation.mutate(
      {
        noteId: note.id,
      },
      {
        onSuccess: () => {
          goto('message');
        },
        onError: (error) => {
          logger.error('Error generating follow-up message:', {
            err: error instanceof Error ? error : undefined,
          });
          showToast('Kunde inte generera AI-förslag på uppföljning.', 'error');
        },
      },
    );
  }
</script>

{#if !$rolesQuery.isLoading}
  <Card size="md">
    <div class="flex flex-col space-y-2">
      <div>
        <div class="flex items-start justify-between text-white">
          <div>
            {getNoteTypeText(note.contactType)}
          </div>
          <div class="flex flex-row gap-2">
            {#if note.transcriptionId && onTranscriptionClick !== undefined}
              <Button
                onclick={() => onTranscriptionClick(note)}
                variant="secondary"
                class={twMerge(
                  'h-6 gap-2',
                  note.transcriptionId == transcriptionId &&
                    'border-white bg-gray-8 text-white',
                )}
              >
                <div class="flex justify-end">
                  <Icon icon="comments" />
                  <Icon icon="comments" class="ml-0.5 scale-x-[-1] " />
                </div>
              </Button>
            {/if}
            {#if note.savedByUserId === $user?.id}
              {#if !note.deleted && note.signedBy}
                <button
                  onclick={handleDeleteNoteClick}
                  class="transition hover:text-white"
                >
                  <div class="flex h-6 w-6 justify-end">
                    <Icon icon="strikethrough" />
                  </div>
                </button>
              {/if}
              {#if !note.deleted && !note.signedBy}
                <div class="flex justify-end gap-2">
                  <button
                    onclick={editNote}
                    class="self-center transition hover:text-white"
                  >
                    <Icon icon="edit" />
                  </button>
                  <button
                    onclick={deleteNote}
                    class="self-center transition hover:text-white"
                  >
                    <Icon icon="delete" />
                  </button>
                  <Button
                    onclick={signNote}
                    variant="secondary"
                    class="h-6 gap-2"
                  >
                    Signera
                  </Button>
                </div>
              {/if}
            {/if}
          </div>
        </div>

        <div>
          {#if consultationAreas.length}
            <div class="mb-1 flex flex-wrap gap-1">
              {#each consultationAreas as consultationArea}
                <Badge size="sm">{consultationArea}</Badge>
              {/each}
            </div>
          {/if}
        </div>
      </div>

      <div>
        <div class={noteClasses}>{note.notes}</div>
        <div class="mt-2 flex justify-between text-sm">
          <div class="flex gap-1">
            <p class="capitalize">
              {note.nurseName || 'Admin'},
            </p>
            <p>{userRole?.name || 'unknown'}</p>
          </div>
          <div class="flex gap-2">
            <p class="text-white">Skapad</p>
            <p>{note.dateCreated}</p>
          </div>
          <div class="flex flex-col">
            <div class="flex gap-2">
              {#if !note.signedBy}
                <p class="text-white">Uppdaterad</p>
                <p>{note.updatedAt}</p>
              {:else}
                <p class="text-white">Signerad</p>
                <p>{note.signedDate}</p>
              {/if}
            </div>
          </div>
        </div>
        {#if ['HEALTH_PROGRAMS', 'HEALTH_EXAMINATION', 'CONSULTATION'].includes(note.contactType)}
          <div class="mt-4">
            <Button
              variant="secondary"
              size="sm"
              class="flex gap-2 border-yellow-500 text-white"
              loading={$generateFollowUpMutation.isPending}
              onclick={handleGenerateFollowUpMessage}
            >
              🤖 AI-förslag på uppföljning
            </Button>
          </div>
        {/if}
      </div>

      {#if note.deleted}
        <div class="border-t border-gray-8"></div>
        {#if note.deletedAt && note.deletedByUserId && note.deletedReason}
          <div class="overflow-auto">
            Struken <span class="font-semibold">
              {format(parseISO(note.deletedAt), 'yyyy-MM-dd HH:mm')}
            </span>

            <span class="font-semibold">
              {deletedByFullName}, {userRole?.name}
            </span>
            på grund av
            <span class="font-semibold">{note.deletedReason}</span>
          </div>
        {:else}
          <div class="font-sm italic">
            Information om strykning av journalanteckning saknas.
          </div>
        {/if}
      {/if}
    </div>
  </Card>
{/if}
